/**
 * MuJoCo MJCF -> Three.js geometry sizing helpers.
 *
 * MuJoCo `geom.size` uses shape-specific conventions:
 * - box: (hx, hy, hz) half-extents
 * - plane: (hx, hy, grid) rendered as a finite plane of size 2hx by 2hy
 * - sphere: (r) radius
 * - ellipsoid: (rx, ry, rz) radii
 * - cylinder: (r, h) where h is half-length (full height = 2h)
 * - capsule: (r, h) where h is half-length of the cylindrical section (full length = 2h)
 *
 * Three.js geometry constructors generally take full extents / full lengths.
 */

const num = (v, fallback) => (Number.isFinite(v) ? Number(v) : fallback);

/**
 * Returns the argument list you should pass to the corresponding Three.js geometry constructor.
 *
 * - box -> new THREE.BoxGeometry(...args)
 * - plane -> new THREE.PlaneGeometry(...args)
 * - sphere -> new THREE.SphereGeometry(...args)
 * - ellipsoid -> new THREE.SphereGeometry(...args), then scale mesh by returned scale
 * - cylinder -> new THREE.CylinderGeometry(...args)
 * - capsule -> new THREE.CapsuleGeometry(...args)
 */
export function getThreeGeomArgsFromMjcfSize(geomType, mjcfSize) {
  const t = String(geomType || "").toLowerCase();
  const s = Array.isArray(mjcfSize) ? mjcfSize : [];

  // Defaults are intentionally conservative; callers often validate > 0 elsewhere.
  const s0 = num(s[0], 0.1);
  const s1 = num(s[1], s0);
  const s2 = num(s[2], s1);

  switch (t) {
    case "box":
    case "rectangle":
      return [2 * s0, 2 * s1, 2 * s2];
    case "plane":
      return [
        2 * s0,
        2 * s1,
        Math.max(1, Math.round(s0 / Math.max(s2, 0.01))),
        Math.max(1, Math.round(s1 / Math.max(s2, 0.01))),
      ];
    case "sphere":
      return [s0];
    case "ellipsoid":
      return [1, 32, 16, 0, Math.PI * 2, 0, Math.PI];
    case "cylinder":
      // (radiusTop, radiusBottom, height)
      return [s0, s0, 2 * s1];
    case "capsule":
      // (radius, length) where length is cylindrical section length
      return [s0, 2 * s1];
    default:
      return null;
  }
}

export function getThreeGeomScaleFromMjcfSize(geomType, mjcfSize) {
  const t = String(geomType || "").toLowerCase();
  const s = Array.isArray(mjcfSize) ? mjcfSize : [];
  const s0 = num(s[0], 0.1);
  const s1 = num(s[1], s0);
  const s2 = num(s[2], s1);
  if (t === "ellipsoid") return [s0, s1, s2];
  return [1, 1, 1];
}
